import { useTranslation } from 'react-i18next';
import { ContextMenu } from '../../ContextMenu/ContextMenu';
import { DocumentRow } from './DocumentRow';
import { FolderRow } from './FolderRow';
import { NewFolderRow } from './NewFolderRow';
import { ParentRow } from './ParentRow';
import { useDocumentViewModel, type IDocumentViewModelProps } from '../hooks/useDocumentViewModel';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './DocumentTable.module.css';
import { DocumentIcon, SortIcon } from '@/utils/icons';

/**
 * The folder-contents table: subfolders above files, sortable columns, inline
 * renaming, and the right-click menu. Behavior lives in useDocumentViewModel;
 * this file is the rows and columns.
 */
export function DocumentTable(props: IDocumentViewModelProps) {
  const { parentFolder = null, emptyMessage } = props;
  const { t } = useTranslation();
  const { handleSort, sorted, sortedFolders, isEmpty, menu, openFolder, folderProps, fileProps, newFolderProps } =
    useDocumentViewModel(props);

  return (
    <ScrollArea
      orientation="both"
      className={`${styles.tableWrap} ${menu.ctxMenu ? styles.menuOpen : ''}`}
      viewportProps={{ onContextMenu: menu.openBackgroundMenu }}
    >
      <table className={styles.table}>
        <thead>
          <tr className={styles.headerRow}>
            <th className={`${styles.th} ${styles.colName}`}>
              <button type="button" className={styles.sortBtn} onClick={() => handleSort('name')}>
                {t('documents.col.name')}
                <SortIcon />
              </button>
            </th>
            <th className={`${styles.th} ${styles.colDesc}`}>{t('documents.col.description')}</th>
            <th className={`${styles.th} ${styles.colVersion}`}>{t('documents.col.version')}</th>
            <th className={`${styles.th} ${styles.colStatus}`}>{t('documents.col.status')}</th>
            <th className={`${styles.th} ${styles.colSize}`}>
              <button type="button" className={styles.sortBtn} onClick={() => handleSort('sizeBytes')}>
                {t('documents.col.size')}
                <SortIcon />
              </button>
            </th>
            <th className={`${styles.th} ${styles.colUpdated}`}>
              <button type="button" className={styles.sortBtn} onClick={() => handleSort('updatedAt')}>
                {t('documents.col.updated')}
                <SortIcon />
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {parentFolder !== null && <ParentRow target={parentFolder} onOpen={openFolder} />}
          {isEmpty ? (
            <tr>
              <td colSpan={6} className={styles.emptyCell}>
                <span className={styles.emptyIcon}><DocumentIcon size={32} /></span>
                <span>{emptyMessage ?? t('documents.emptyFolder')}</span>
              </td>
            </tr>
          ) : (
            <>
              {newFolderProps && <NewFolderRow {...newFolderProps} />}
              {sortedFolders.map((folder) => <FolderRow key={folder.id} {...folderProps(folder)} />)}
              {sorted.map((doc) => <DocumentRow key={doc.id} {...fileProps(doc)} />)}
            </>
          )}
        </tbody>
      </table>

      {menu.ctxMenu && (
        <ContextMenu
          x={menu.ctxMenu.x}
          y={menu.ctxMenu.y}
          items={menu.ctxItems}
          onClose={menu.closeCtxMenu}
        />
      )}
    </ScrollArea>
  );
}